import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import { useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AccountsIcon from 'src/assets/icons/AccountsIcon';
import CollapseIcon from 'src/assets/icons/CollapseIcon';
import DashboardIcon from 'src/assets/icons/DashboardIcon';
import IntegrationGuidesIcon from 'src/assets/icons/IntegrationGuidesIcon';
import LogoIcon from 'src/assets/icons/LogoIcon';
import PaymentAcceptanceIcon from 'src/assets/icons/PaymentAcceptanceIcon';
import ReportsIcon from 'src/assets/icons/ReportsIcon';
import SidebarIcon from 'src/assets/icons/SidebarIcon';
import SplitPaymentsIcon from 'src/assets/icons/SplitPaymentsIcon';
import { useAuth } from 'src/hooks/useAuth';
import { useTheme as useHooksTheme } from 'src/hooks/useTheme';
import paths from 'src/navigation/paths';

interface MenuItemElement {
  label: string;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  children?: {
    label: string;
    to: string
  }[];
  to?: string;
}

const splitPaymentsMenu: MenuItemElement = {
  label: 'Split payments',
  icon: SplitPaymentsIcon,
  children: [
    {
      label: 'Overview',
      to: paths.splitPayments
    },
    {
      label: 'Split transactions',
      to: paths.splitPaymentsTransactions
    },
    {
      label: 'Payees',
      to: paths.splitPaymentsPayees
    },
    {
      label: 'Split rules',
      to: paths.splitPaymentsRules
    },
    {
      label: 'Payouts',
      to: paths.splitPaymentsPayouts
    },
  ]
};

const onlinePaymentsMenu: MenuItemElement = {
  label: 'Online payments',
  icon: PaymentAcceptanceIcon,
  children: [
    {
      label: 'Transactions',
      to: paths.onlinePaymentsTransactions
    },
    {
      label: 'Recurring payments',
      to: paths.onlinePaymentsRecurringPayments
    },
    {
      label: 'Payment links',
      to: paths.onlinePaymentsPaymentLinks
    },
    {
      label: 'Refunds',
      to: paths.onlinePaymentsRefunds
    },
    {
      label: 'Disputes & chargebacks',
      to: paths.onlinePaymentsDisputes
    },
    {
      label: 'Payment settings',
      to: paths.onlinePaymentsSettings
    },
  ]
};

const menuLinks: MenuItemElement[] = [
  {
    label: 'Dashboard',
    to: paths.main,
    icon: DashboardIcon
  },
  { ...splitPaymentsMenu },
  { ...onlinePaymentsMenu },
  {
    label: 'Business intelligence',
    to: paths.businessIntelligence,
    icon: ReportsIcon
  },
  {
    label: 'Reports',
    to: paths.reports,
    icon: ReportsIcon
  },
  {
    label: 'Accounts',
    to: paths.accounts,
    icon: AccountsIcon
  },
  {
    label: 'Integration guides',
    to: paths.integrationGuides,
    icon: IntegrationGuidesIcon
  },
];

const userLinks = [
  {
    title: 'Logout',
    path: paths.logout,
  },
];

const Sidebar: React.FC<{
  open: boolean;
  onClose: () => void;
  onShrinkChange?: (shrunk: boolean) => void;
}> = ({ open, onClose, onShrinkChange }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [shrink, setShrink] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { mode } = useHooksTheme();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const auth = useAuth();

  if (!auth) {
    throw new Error('useAuthActions must be used within an AuthProvider');
  }

  const { user } = auth;

  // State to track which menu is currently open (only one at a time)
  const [openMenu, setOpenMenu] = useState<string | null>(() => {
    if (location.pathname.startsWith(paths.splitPayments)) return splitPaymentsMenu.label;
    if (location.pathname.startsWith(paths.onlinePayments)) return onlinePaymentsMenu.label;
    return null;
  });
  // Calculate dynamic width based on shrink state
  const sidebarWidth = shrink ? 72 : 260;

  // Handle shrink toggle
  const handleShrinkToggle = () => {
    const newShrinkState = !shrink;
    setShrink(newShrinkState);
    onShrinkChange?.(newShrinkState);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleNavigation = (path: string) => {
    handleCloseUserMenu();
    navigate(path);
  };

  // Get initials for avatar
  const getInitials = () => {
    if (user?.full_name) {
      const parts = user.full_name.trim().split(' ');
      if (parts.length >= 2) {
        return parts[0][0] + parts[1][0];
      }

      return parts[0][0];
    }

    return 'A';
  };


  const sidebarContent = (
    <Box
      sx={{
        width: sidebarWidth,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        borderRight: '1px solid',
        borderColor: 'custom.border',
      }}
    >
      {/* Top bar with toggle, search, new conversation */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: shrink ? 'center' : 'space-between',
          p: theme.spacing(1, 1.5, 1, 2),
          mb: 4,
        }}
      >
        {!shrink && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Link
              to={paths.main}
              style={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
              }}
            >
              <LogoIcon color={mode === 'dark' ? 'custom.white' : 'custom.obsidian'} />
            </Link>
          </Box>
        )}
        <Tooltip title={shrink ? 'Expand sidebar' : 'Collapse sidebar'}>
          <IconButton size="small" onClick={handleShrinkToggle} sx={{ p: '6px', color: 'text.primary' }}>
            <SidebarIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        flex: 1,
      }}>
        <List sx={{ px: 1.5 }}>
          {menuLinks.map(({ label, to, icon: Icon, children }) => (
            <React.Fragment key={label}>
              <Tooltip
                title={shrink ? label : ''}
                placement="right"
              >
                <ListItem
                  component="button"
                  onClick={() => {
                    if (!shrink && children) {
                      // Toggle the current menu: if it's already open, close it; otherwise, open it and close others
                      setOpenMenu(openMenu === label ? null : label);
                    } else {
                      navigate(to)
                    }
                  }}
                  sx={{
                    borderRadius: '8px',
                    p: 1,
                    gap: 1,
                    bgcolor:
                      !children && location.pathname === to
                        ? 'rgba(27, 9, 51, 0.10)'
                        : 'transparent',
                    '&:hover': { bgcolor: 'rgba(27, 9, 51, 0.20)' },
                    justifyContent: shrink ? 'center' : 'flex-start',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      justifyContent: 'center',
                      color: 'text.primary',
                      p: 0.5,
                      minWidth: 0,
                    }}
                  >
                    <Icon width={16} height={16} />
                  </ListItemIcon>
                  {!shrink && (
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          fontWeight={400}
                        >
                          {label}
                        </Typography>
                      }
                    />
                  )}
                  {!shrink && children ? (
                    <Box
                      sx={{
                        transform: openMenu === label ? 'rotate(0deg)' : 'rotate(180deg)',
                        transition: 'transform 0.2s',
                      }}
                    >
                      <CollapseIcon />
                    </Box>
                  ) : null}
                </ListItem>
              </Tooltip>
              {!shrink && children ? (
                <Collapse in={openMenu === label} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {children.map(({ label, to }) => (
                      <ListItem
                        key={label}
                        component="button"
                        onClick={() => navigate(to)}
                        sx={{
                          borderRadius: '8px',
                          p: 1,
                          gap: 1,
                          bgcolor:
                            location.pathname === to
                              ? 'rgba(27, 9, 51, 0.10)'
                              : 'transparent',
                          '&:hover': { bgcolor: 'rgba(27, 9, 51, 0.20)' },
                          justifyContent: shrink ? 'center' : 'flex-start',
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            p: 0.5,
                            minWidth: 0,
                          }}
                        >
                          <Box width={16} height={16} />
                        </ListItemIcon>
                        {!shrink && (
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                fontWeight={400}
                              >
                                {label}
                              </Typography>
                            }
                          />
                        )}
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              ) : null}
            </React.Fragment>
          ))}
        </List>

        <Box sx={{
          marginTop: 2,
          marginBottom: 2,
          marginLeft: !shrink ? 2 : 'auto',
          marginRight: !shrink ? 2 : 'auto',
        }}>
          <IconButton onClick={handleOpenUserMenu} sx={{ p: 0, gap: 1 }} disableRipple>
            <Avatar
              alt="Avatar"
              src={user?.avatar ? user.avatar : undefined}
            >
              {getInitials()}
            </Avatar>
            {!shrink ? (
              <Typography variant="body2" color="text.primary" component="span">
                {user.full_name}
              </Typography>
            ) : null}
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorElUser}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            keepMounted
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            {userLinks.map(({ title, path }) => (
              <MenuItem
                key={title}
                onClick={() => handleNavigation(path)}
                sx={{ py: 1.5, px: 2 }}
              >
                {title}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>
    </Box>
  );

  if (isDesktop) {
    return (
      <Box
        sx={{
          width: sidebarWidth,
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: 1202,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {sidebarContent}
      </Box>
    );
  }

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
    >
      {sidebarContent}
    </Drawer>
  );
};

export default Sidebar;
